/**
 * 会话状态切片 · AI 智能分类
 *
 * 协议重定位 (2026-05 重构):
 *   · init 接口仅做团队级数据预热 (一团队一次), 不再创建/恢复会话
 *   · conversationId 由用户在欢迎页发首条消息时, send 接口 done 事件返回并回填
 *   · status 三态: idle / streaming / error
 *   · 没有 thunk, 所有副作用走 SSE 客户端 → dispatch 同步 action
 *
 * 三种重置语义 (互不重叠):
 *   · conversationCleared · 切到新对话 / 切历史会话 · 清单条会话状态, 保留团队预热数据
 *   · conversationReplaced · SSE done 回填新 conv_id · 严格守卫防废弃 SSE 污染
 *   · teamSwitched · 切团队 · 整个切片清回初始 (含团队预热数据, 等下一轮 init 填回)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ConversationId, SessionStatus } from '../../types/ai-classify'
import type { RecommendedPrompt } from '../../pages/AIClassifyFolder/utils/recommendedPrompt'

interface SessionError {
  code: string
  message: string
}

interface SessionState {
  conversationId: ConversationId | null
  status: SessionStatus
  error: SessionError | null
  /** init 接口是否还在飞行中 · 用于骨架屏 */
  initLoading: boolean
  /**
   * /init 返回的 recommended_prompts · 后端聚合 500 张照片真实分布算出的
   * 「按 X 分类」推荐项（含 text/iconType/subtitle），EmptyState 与 QuickPrompts 共用。
   * 空数组时渲染层走 FALLBACK_RECOMMENDED_PROMPTS 兜底。
   */
  initRecommended: RecommendedPrompt[]
  /** /init 返回的 note · 给前端首页用的人话总结（welcome 文案） */
  initNote: string
}

const initialState: SessionState = {
  conversationId: null,
  status: 'idle',
  error: null,
  initLoading: true,
  initRecommended: [],
  initNote: ''
}

const sessionSlice = createSlice({
  name: 'aiClassify/session',
  initialState,
  reducers: {
    /** 进入页面时一次性写入 · 客户端生成的 web_<ts>_<rand> */
    conversationOpened(state, action: PayloadAction<ConversationId>) {
      state.conversationId = action.payload
      state.status = 'idle'
      state.error = null
    },
    /** 标记进入流式态 · 由 SSE 客户端在 fetch 开始后调用 */
    streamStarted(state) {
      state.status = 'streaming'
      state.error = null
    },
    /** 标记流结束（done）· 回到 idle 等下一条用户输入 */
    streamFinished(state) {
      state.status = 'idle'
    },
    /** 标记流出错 · status=error + 错误信息 */
    streamErrored(state, action: PayloadAction<SessionError>) {
      state.status = 'error'
      state.error = action.payload
    },
    /**
     * /init 返回的预处理结果 · 推荐项 / welcome 文案的来源
     *
     * 由 prewarmTeam 在进入页面 / 切团队时调用；init 失败会跳过本 action，
     * EmptyState / QuickPrompts 渲染层走 FALLBACK_RECOMMENDED_PROMPTS 兜底
     * （避免空状态没法引导）。
     */
    setInitWelcome(
      state,
      action: PayloadAction<{
        recommended: RecommendedPrompt[]
        note: string
      }>
    ) {
      state.initLoading = false
      state.initRecommended = action.payload.recommended
      state.initNote = action.payload.note
    },
    /** init 失败 / fallback 时结束 loading（不带 insights 数据） */
    initLoadingDone(state) {
      state.initLoading = false
    },
    /**
     * 会话切换重置 · 用户点"新对话" / 切到历史会话 / 切会话前清场用
     *
     * 清:    conversationId / status / error · 单条会话级别的状态
     * 保留:  initRecommended / initNote / initLoading · 团队级预热数据
     *
     * 为什么不清预热数据: 协议重定位后 init 不会因为"新对话"重跑 (一团队预热一次),
     * 如果连带清掉会让欢迎页 QuickPrompts 推荐项消失, 又没有接口能把它们填回来。
     * 团队预热数据的生命周期跟随 teamSwitched, 不跟随会话切换。
     */
    conversationCleared(state) {
      state.conversationId = null
      state.status = 'idle'
      state.error = null
    },
    /**
     * SSE done 事件回填会话 id · 用户在欢迎页发首条消息走完一轮后, 后端在 done 事件
     * 返回新建的 conversation_id, 前端用本 action 把 id 写回 state。
     *
     * 严格守卫 (闭包安全): 仅在 state.conversationId 仍为 null 时接受回填。
     * 如果用户在 SSE 进行中又点了"新对话" / 切到了别的历史会话, redux 已经被
     * conversationCleared 或 conversationOpened 写过, 这次回填来自一个已被 abort
     * 的废弃 SSE, 必须忽略 — 否则会把当前会话 id 覆盖成老 SSE 的 id 污染状态。
     *
     * buildStreamHandlers.onDone 无脑 dispatch 本 action, 是否真正写入由 reducer 决定,
     * 这样 handler 不需要持有"发消息时的 conversationId 快照", 也不需要读 store。
     */
    conversationReplaced(state, action: PayloadAction<ConversationId>) {
      const newId = action.payload
      if (!newId) return
      if (state.conversationId != null) return
      state.conversationId = newId
    },
    /**
     * 切团队 · 整个 sessionSlice 清回初始态 (含团队预热数据)
     *
     * 之后由 prewarmTeam effect 重新调 init 把新团队的预热数据填回 initRecommended / initNote。
     * messagesSlice / selectionSlice / uiSlice 等通过 extraReducer 监听本 action 联动清空。
     */
    teamSwitched() {
      return { ...initialState }
    }
  }
})

export const {
  conversationOpened,
  streamStarted,
  streamFinished,
  streamErrored,
  setInitWelcome,
  initLoadingDone,
  conversationCleared,
  conversationReplaced,
  teamSwitched
} = sessionSlice.actions

export default sessionSlice.reducer
